import { useEffect, useRef, useState } from 'react';
import { TouchPad } from '../../components/TouchPad';
import { arcadeAudio } from '../../platform/audio';
import { fitCanvas, loadImages } from '../../platform/canvas';
import { Input } from '../../platform/input';
import type { GameProps } from '../../platform/types';

const W = 480;
const H = 720;

interface E { x: number; y: number; hp: number; r: number; kind: 'grunt' | 'tank' | 'boss'; vx: number; score: number }
interface B { x: number; y: number; vy: number; vx: number; foe?: boolean; dmg: number }
interface P { x: number; y: number; vx: number; vy: number; life: number; c: string }

export function VoidNeedleGame({ onExit, onResult }: GameProps) {
  const ref = useRef<HTMLCanvasElement>(null);
  const input = useRef(new Input());
  const [over, setOver] = useState<{ score: number; won: boolean } | null>(null);
  const hud = useRef({ hp: 5, bomb: 3, score: 0, stage: 1 });

  useEffect(() => {
    const canvas = ref.current!;
    const ctx = fitCanvas(canvas, W, H)!;
    const inp = input.current.attach(canvas);
    let dead = false;
    void arcadeAudio.playMusic('void', '/assets/music/Space-Cadet.ogg');

    const p = { x: W / 2, y: H - 90, hp: 5, focus: false, bomb: 3, inv: 0, fire: 0 };
    const foes: E[] = [];
    const shots: B[] = [];
    const parts: P[] = [];
    let score = 0;
    let t = 0;
    let stage = 1;
    let chain = 0;
    let spawn = 0;
    let bossIn = false;
    let won = false;

    const burst = (x: number, y: number, c: string) => {
      for (let i = 0; i < 10; i++) {
        const a = Math.random() * 6.28;
        parts.push({ x, y, vx: Math.cos(a) * 80, vy: Math.sin(a) * 80, life: 0.4, c });
      }
    };

    let imgs = new Map<string, HTMLImageElement>();
    void loadImages({
      you: '/assets/shmup/ships/ship_0000.png',
      g: '/assets/shmup/ships/ship_0015.png',
      tank: '/assets/shmup/ships/ship_0018.png',
      boss: '/assets/shmup/ships/ship_0022.png',
      star: '/assets/shmup/tiles/tile_0000.png',
    }).then((m) => { imgs = m; });

    const finish = (w: boolean) => {
      if (over) return;
      won = w;
      onResult({
        score, won: w,
        stamps: { clear: w, skill: score >= 25000, mastery: w && p.hp >= 4 },
      });
      setOver({ score, won: w });
    };

    let last = performance.now();
    let raf = 0;
    const loop = (now: number) => {
      if (dead) return;
      const dt = Math.min(0.04, (now - last) / 1000);
      last = now;
      if (!over && !won && p.hp > 0) tick(dt);
      draw();
      raf = requestAnimationFrame(loop);
    };

    const tick = (dt: number) => {
      t += dt;
      const ax = inp.axis();
      const spd = (inp.keys['shift'] || inp.actions.b || p.focus) ? 140 : 260;
      p.x = Math.max(18, Math.min(W - 18, p.x + ax.x * spd * dt));
      p.y = Math.max(24, Math.min(H - 24, p.y + ax.y * spd * dt));
      p.inv = Math.max(0, p.inv - dt);
      p.fire -= dt;
      p.focus = !!(inp.keys['shift'] || inp.actions.b);
      if (p.fire <= 0) {
        p.fire = p.focus ? 0.07 : 0.12;
        shots.push({ x: p.x, y: p.y - 16, vy: -520, vx: 0, dmg: p.focus ? 3 : 2 });
        if (!p.focus) {
          shots.push({ x: p.x - 10, y: p.y - 8, vy: -480, vx: -40, dmg: 1 });
          shots.push({ x: p.x + 10, y: p.y - 8, vy: -480, vx: 40, dmg: 1 });
        }
      }
      if ((inp.keys[' '] || inp.actions.a) && p.bomb > 0) {
        inp.keys[' '] = false;
        inp.actions.a = false;
        p.bomb -= 1;
        for (const e of [...foes]) { e.hp -= 40; burst(e.x, e.y, '#fff'); }
        shots.length = 0;
        arcadeAudio.blip(180, 0.2, 'sawtooth', 0.1);
      }

      spawn += dt;
      const cap = stage === 1 ? 18 : stage === 2 ? 26 : 34;
      if (!bossIn && spawn > (stage === 3 ? 0.45 : 0.7) && foes.length < cap) {
        spawn = 0;
        const tank = Math.random() < 0.18;
        foes.push({
          x: 40 + Math.random() * (W - 80), y: -20,
          hp: tank ? 18 + stage * 6 : 4 + stage * 2,
          r: tank ? 18 : 12,
          kind: tank ? 'tank' : 'grunt',
          vx: (Math.random() - 0.5) * 40,
          score: tank ? 180 : 60,
        });
      }
      if (!bossIn && t > stage * 70) {
        bossIn = true;
        foes.push({ x: W / 2, y: -40, hp: 220 + stage * 80, r: 36, kind: 'boss', vx: 50, score: 4000 });
        arcadeAudio.blip(80, 0.3, 'sawtooth', 0.12);
      }

      for (const e of foes) {
        if (e.kind === 'boss') {
          e.y = Math.min(110, e.y + 40 * dt);
          e.x += e.vx * dt;
          if (e.x < 50 || e.x > W - 50) e.vx *= -1;
          if (Math.random() < 0.04) {
            for (let i = -2; i <= 2; i++) shots.push({ x: e.x, y: e.y + 20, vy: 160, vx: i * 50, foe: true, dmg: 1 });
          }
        } else {
          e.y += (e.kind === 'tank' ? 50 : 80 + stage * 10) * dt;
          e.x += e.vx * dt;
          if (Math.random() < 0.008 * stage) shots.push({ x: e.x, y: e.y + 10, vy: 180, vx: 0, foe: true, dmg: 1 });
        }
      }

      for (let i = shots.length - 1; i >= 0; i--) {
        const s = shots[i];
        s.x += s.vx * dt; s.y += s.vy * dt;
        if (s.y < -20 || s.y > H + 20) { shots.splice(i, 1); continue; }
        if (s.foe) {
          if ((s.x - p.x) ** 2 + (s.y - p.y) ** 2 < 14 ** 2 && p.inv <= 0) {
            p.hp -= 1; p.inv = 1; shots.splice(i, 1); chain = 0;
            if (p.hp <= 0) finish(false);
          }
        } else {
          for (const e of foes) {
            if ((s.x - e.x) ** 2 + (s.y - e.y) ** 2 < (e.r + 4) ** 2) {
              e.hp -= s.dmg;
              shots.splice(i, 1);
              chain += 1;
              score += 10 * (1 + Math.floor(chain / 8));
              break;
            }
          }
        }
      }

      for (let i = foes.length - 1; i >= 0; i--) {
        const e = foes[i];
        if (e.hp <= 0) {
          score += e.score * (1 + Math.floor(chain / 10));
          burst(e.x, e.y, e.kind === 'boss' ? '#ffb020' : '#7c5cff');
          foes.splice(i, 1);
          if (e.kind === 'boss') {
            if (stage >= 3) finish(true);
            else { stage += 1; t = (stage - 1) * 70; bossIn = false; }
          }
        } else if (e.y > H + 30) foes.splice(i, 1);
        else if ((e.x - p.x) ** 2 + (e.y - p.y) ** 2 < (e.r + 10) ** 2 && p.inv <= 0) {
          p.hp -= 1; p.inv = 1; chain = 0;
          if (p.hp <= 0) finish(false);
        }
      }

      for (let i = parts.length - 1; i >= 0; i--) {
        const q = parts[i];
        q.x += q.vx * dt; q.y += q.vy * dt; q.life -= dt;
        if (q.life <= 0) parts.splice(i, 1);
      }
      hud.current = { hp: p.hp, bomb: p.bomb, score, stage };
    };

    const draw = () => {
      const bg = ctx.createLinearGradient(0, 0, 0, H);
      bg.addColorStop(0, '#12061c');
      bg.addColorStop(0.5, '#070614');
      bg.addColorStop(1, '#041018');
      ctx.fillStyle = bg;
      ctx.fillRect(0, 0, W, H);
      ctx.fillStyle = '#ffffff28';
      for (let i = 0; i < 80; i++) ctx.fillRect((i * 97 + t * 18) % W, (i * 53 + t * 90) % H, i % 7 === 0 ? 2 : 1, i % 7 === 0 ? 2 : 1);
      const ship = (key: string, x: number, y: number, s: number, rot = 0) => {
        const im = imgs.get(key);
        if (im) {
          ctx.save();
          ctx.translate(x, y);
          ctx.rotate(rot);
          ctx.drawImage(im, -s / 2, -s / 2, s, s);
          ctx.restore();
        } else {
          ctx.fillStyle = key === 'you' ? '#3ee0c5' : '#ff5d8f';
          ctx.fillRect(x - s / 3, y - s / 3, s * 0.66, s * 0.66);
        }
      };
      for (const e of foes) ship(e.kind === 'boss' ? 'boss' : e.kind === 'tank' ? 'tank' : 'g', e.x, e.y, e.r * 2.4, e.kind === 'boss' ? 0 : Math.PI);
      ship('you', p.x, p.y, 36);
      for (const s of shots) {
        ctx.fillStyle = s.foe ? '#ff5d8f' : '#ffb020';
        ctx.fillRect(s.x - 2, s.y - 6, 4, 10);
      }
      for (const q of parts) {
        ctx.globalAlpha = q.life * 2;
        ctx.fillStyle = q.c;
        ctx.fillRect(q.x, q.y, 3, 3);
        ctx.globalAlpha = 1;
      }
      ctx.fillStyle = 'rgba(4,6,16,0.55)';
      ctx.fillRect(8, 6, W - 16, 26);
      ctx.fillStyle = '#fff4df';
      ctx.font = '12px IBM Plex Mono, monospace';
      ctx.fillText(`HP ${p.hp}   BOMB ${p.bomb}   STAGE ${stage}   ${score}`, 16, 24);
    };

    raf = requestAnimationFrame(loop);
    return () => {
      dead = true;
      cancelAnimationFrame(raf);
      inp.dispose();
      arcadeAudio.stopMusic();
    };
  }, [onResult]);

  return (
    <div className="game-root voidneedle">
      <canvas ref={ref} width={W} height={H} />
      <TouchPad
        input={input.current}
        extra={[
          { id: 'bomb', label: 'Bomb', on: () => { input.current.actions.a = true; }, off: () => { input.current.actions.a = false; } },
          { id: 'focus', label: 'Focus', on: () => { input.current.actions.b = true; }, off: () => { input.current.actions.b = false; } },
        ]}
      />
      {over && (
        <div className="overlay">
          <div className="panel">
            <h2>{over.won ? 'Orbit clear' : 'Hull breach'}</h2>
            <p>Score {over.score.toLocaleString()}</p>
            <button type="button" className="play" onClick={() => location.reload()}>Again</button>
            <button type="button" className="ghost" onClick={onExit} style={{ marginTop: 8, width: '100%' }}>Lobby</button>
          </div>
        </div>
      )}
    </div>
  );
}
